const KEY = 'dombase:watchlist:v1';
const MAX_ITEMS = 50;

export type WatchedSnapshot = {
  available: boolean | null;
  priceUsd: number | null;
  registrar: string | null;
  valuation: number | null;
  brandRiskState: 'clear' | 'caution' | 'risk' | 'unknown';
};

export type WatchedDomain = {
  domain: string;
  addedAt: string;
  lastChecked: string;
  snapshot: WatchedSnapshot;
};

function isValidItem(item: any): item is WatchedDomain {
  return (
    item &&
    typeof item.domain === 'string' &&
    typeof item.addedAt === 'string' &&
    typeof item.lastChecked === 'string' &&
    item.snapshot &&
    typeof item.snapshot === 'object'
  );
}

export function readWatchlist(): WatchedDomain[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidItem);
  } catch {
    return [];
  }
}

export function writeWatchlist(items: WatchedDomain[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // quota exceeded or storage blocked — fail silently, caller handles
  }
}

export function addToWatchlist(
  domain: string,
  snapshot: WatchedSnapshot
): { ok: boolean; reason?: 'full' | 'storage_blocked' } {
  const items = readWatchlist();
  if (items.some(i => i.domain === domain)) {
    return { ok: true }; // already watching — idempotent
  }
  if (items.length >= MAX_ITEMS) {
    return { ok: false, reason: 'full' };
  }
  const now = new Date().toISOString();
  const next = [...items, { domain, addedAt: now, lastChecked: now, snapshot }];
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    return { ok: true };
  } catch {
    return { ok: false, reason: 'storage_blocked' };
  }
}

export function removeFromWatchlist(domain: string): void {
  const items = readWatchlist().filter(i => i.domain !== domain);
  writeWatchlist(items);
}

export function isWatched(domain: string): boolean {
  return readWatchlist().some(i => i.domain === domain);
}

export function updateSnapshot(domain: string, snapshot: WatchedSnapshot): void {
  const items = readWatchlist().map(i =>
    i.domain === domain
      ? { ...i, lastChecked: new Date().toISOString(), snapshot }
      : i
  );
  writeWatchlist(items);
}
